import { apiRequest } from '@/lib/api'

export interface LegalDocument {
  id: string
  type: string
  version: string
  effective_at: string
  locale?: string
  content?: string
  draft_content?: string
  edit_content?: string
  has_fr: boolean
  has_en: boolean
  fr_id?: string
  en_id?: string
  updated_at: string
  is_active: boolean
  has_draft?: boolean
}

export interface DocumentType {
  value: string
  label: string
}

export interface PaginatedLegalDocuments {
  data: LegalDocument[]
  current_page: number
  last_page: number
  per_page: number
  total: number
}

export async function fetchAdminLegalDocuments(params?: {
  type?: string
  locale?: string
  platform_only?: boolean
  page?: number
  per_page?: number
}): Promise<PaginatedLegalDocuments> {
  const searchParams = new URLSearchParams()
  if (params?.type) searchParams.append('type', params.type)
  if (params?.locale) searchParams.append('locale', params.locale)
  if (params?.platform_only) searchParams.append('platform_only', 'true')
  if (params?.page) searchParams.append('page', String(params.page))
  if (params?.per_page) searchParams.append('per_page', String(params.per_page))

  const queryString = searchParams.toString()
  const path = `/v1/admin/legal-documents${queryString ? `?${queryString}` : ''}`

  return apiRequest<PaginatedLegalDocuments>(path)
}

export async function fetchAdminLegalDocument(id: string): Promise<{ data: LegalDocument }> {
  return apiRequest<{ data: LegalDocument }>(`/v1/admin/legal-documents/${id}`)
}

export async function createLegalDocument(data: {
  type: string
  business_id?: string | null
  version: string
  effective_at: string
  content_fr: string
  content_en: string
}): Promise<{ message: string; data: LegalDocument }> {
  return apiRequest<{ message: string; data: LegalDocument }>('/v1/admin/legal-documents', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
}

export async function updateLegalDocument(
  id: string,
  data: Partial<{
    type: string
    business_id?: string | null
    version: string
    effective_at: string
    content_fr: string
    content_en: string
  }>
): Promise<{ message: string; data: LegalDocument }> {
  return apiRequest<{ message: string; data: LegalDocument }>(`/v1/admin/legal-documents/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
}

export async function deleteLegalDocument(id: string): Promise<{ message: string }> {
  return apiRequest<{ message: string }>(`/v1/admin/legal-documents/${id}`, {
    method: 'DELETE',
  })
}

export async function toggleLegalDocumentStatus(id: string): Promise<{ message: string; data: { type: string; is_active: boolean } }> {
  return apiRequest<{ message: string; data: { type: string; is_active: boolean } }>(`/v1/admin/legal-documents/${id}/toggle-status`, {
    method: 'PATCH',
  })
}

export async function fetchDocumentTypes(): Promise<{ data: DocumentType[] }> {
  return apiRequest<{ data: DocumentType[] }>('/v1/admin/legal-documents/types')
}

export async function acceptPlatformTerms(): Promise<{ message: string; accepted_at: string; version: string }> {
  return apiRequest<{ message: string; accepted_at: string; version: string }>('/v1/terms/accept-platform', {
    method: 'POST',
  })
}

export async function acceptBusinessTerms(businessId?: string): Promise<{ message: string; accepted_at: string; version: string }> {
  const body = businessId ? JSON.stringify({ business_id: businessId }) : undefined
  return apiRequest<{ message: string; accepted_at: string; version: string }>('/v1/terms/accept-business', {
    method: 'POST',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body,
  })
}

export async function acceptAgentTerms(): Promise<{ message: string; accepted_at: string; version: string }> {
  return apiRequest<{ message: string; accepted_at: string; version: string }>('/v1/terms/accept-agent', {
    method: 'POST',
  })
}

export async function acceptAgentPrivacy(): Promise<{ message: string; accepted_at: string; version: string }> {
  return apiRequest<{ message: string; accepted_at: string; version: string }>('/v1/terms/accept-agent-privacy', {
    method: 'POST',
  })
}

export interface TermsAcceptanceStatus {
  platform_terms: { accepted: boolean; accepted_at: string | null; version: string | null }
  business_terms: { accepted: boolean; accepted_at: string | null; version: string | null }
  agent_terms: { accepted: boolean; accepted_at: string | null; version: string | null }
  agent_privacy: { accepted: boolean; accepted_at: string | null; version: string | null }
}

export async function fetchTermsStatus(): Promise<TermsAcceptanceStatus> {
  return apiRequest<TermsAcceptanceStatus>('/v1/terms/status')
}

export interface PublicLegalDocument {
  id: string
  type: string
  version: string
  effective_at: string
  locale: string
  content: string
  is_active: boolean
}

export async function fetchPublicLegalDocuments(
  locale?: string,
  includeInactive?: boolean
): Promise<{ data: PublicLegalDocument[] }> {
  const params = new URLSearchParams()
  if (locale) params.append('locale', locale)
  if (includeInactive) params.append('include_inactive', 'true')
  const queryString = params.toString()
  const path = `/v1/legal-documents${queryString ? `?${queryString}` : ''}`
  return apiRequest<{ data: PublicLegalDocument[] }>(path)
}

export async function publishLegalDocument(id: string): Promise<{
  message: string
  version: string
  notified_count: number
}> {
  return apiRequest<{ message: string; version: string; notified_count: number }>(
    `/v1/admin/legal-documents/${id}/publish`,
    { method: 'POST' }
  )
}